import { t } from "../i18n.js";
import { icon } from "../icon.js";

// In-app modal dialogs, replacing window.confirm()/window.alert().
//
// The native ones were the only part of the UI the app didn't draw: on a
// phone they render as a "localhost:8080 says" system sheet, visually
// disconnected from everything around them, and their text can't be styled
// or laid out. They're also synchronous, which is why every caller here had
// to be shaped around a blocking boolean.
//
// Built on <dialog> + showModal(), which gives three things for free that a
// hand-rolled div would have to reimplement: the focus trap, Escape to
// dismiss, and the top layer (no z-index fights with the map or the
// dropdowns). The dialog is created per call and removed on close - there's
// no long-lived instance to keep in sync, and at most one is ever open.
//
// Both helpers return a promise, so callers read as
//   if (!(await confirmDialog(...))) return;
// which is the same shape the window.confirm() calls had.

// Resolves true if the user confirmed, false on Cancel or Escape.
//
// `messageKey` is an i18n key for the whole prompt - the existing
// *.deleteConfirm strings are already self-contained sentences ("Delete this
// trip? This cannot be undone."), so there's no separate title, and the
// message doubles as the dialog's accessible name via aria-labelledby.
// `message` is a ready-made string for the callers whose prompt has to name
// something — "Remove Anna from this trip?" — and takes precedence over
// messageKey exactly as it does in alertDialog. open() has always supported
// this; confirmDialog simply never passed it through.
// `iconName` overrides the icon on the confirm button. It exists because
// `danger` was doing two jobs: it picks the red button, which is right for
// every caller, *and* it picked a bin, which is right only for the delete
// confirmations this was written for. "Leave trip" showed one too -- an action
// that takes you off a trip rather than deleting it for everybody. `danger`
// still implies a bin when nothing is passed, so every existing caller is
// unchanged and only the ones that mean something else say so.
export function confirmDialog({ messageKey, message, confirmKey = "common.delete", danger = true, iconName }) {
  return open({
    messageKey,
    message,
    buttons: [
      // Cancel first, so it's what <dialog>'s autofocus lands on and what
      // Enter triggers. Deliberately the reverse of the app's usual
      // primary-first order (see .editor-actions): every caller of this is
      // destructive and irreversible, so the safe choice is the default one.
      { labelKey: "common.cancel", value: "cancel", className: "btn-secondary", iconName: "x" },
      {
        labelKey: confirmKey,
        value: "confirm",
        className: danger ? "btn-danger" : "btn-primary",
        iconName: iconName ?? (danger ? "trash-2" : "check"),
      },
    ],
  }).then((value) => value === "confirm");
}

// A one-field prompt: a message, a text input and Save/Cancel. Resolves to the
// entered string (possibly empty, which is how a note gets cleared) or to null
// if the user cancelled - so callers can tell "saved an empty value" apart from
// "changed their mind", which a bare "" could not.
//
// This exists because there is no window.prompt() equivalent worth using: the
// native one is the same disconnected system sheet confirm() was, and it can't
// carry a placeholder or a starting value.
export function promptDialog({ messageKey, message, value = "", placeholderKey, confirmKey = "common.save" }) {
  return open({
    messageKey,
    // Same as confirmDialog's: a ready-made string for a prompt that has to
    // name something ("New password for Anna").
    message,
    input: { value, placeholderKey },
    buttons: [
      { labelKey: "common.cancel", value: "cancel", className: "btn-secondary", iconName: "x" },
      { labelKey: confirmKey, value: "confirm", className: "btn-primary", iconName: "check" },
    ],
  });
}

// A one-choice prompt: a message, a <select> and Save/Cancel. Resolves to the
// chosen value, or to null if the user dismissed it -- the same
// answer-or-null contract promptDialog has, and for the same reason.
//
// `options` is [{ value, label }] with the label already translated or already
// formatted (the itinerary passes dates through Intl), because the component
// cannot know how a caller wants its values named. `value` preselects one.
//
// A select rather than a list of buttons: the itinerary can offer a fortnight
// of days, and fourteen stacked buttons is a dialog that scrolls on a phone,
// where a native select opens the platform picker people already know.
export function selectDialog({ messageKey, message, options, value = "", labelKey, confirmKey = "common.save" }) {
  return open({
    messageKey,
    message,
    select: { options, value, labelKey },
    buttons: [
      { labelKey: "common.cancel", value: "cancel", className: "btn-secondary", iconName: "x" },
      { labelKey: confirmKey, value: "confirm", className: "btn-primary", iconName: "check" },
    ],
  });
}

// A message with a single dismiss button. Resolves when it's closed.
export function alertDialog({ messageKey, message }) {
  return open({
    messageKey,
    message,
    buttons: [{ labelKey: "common.close", value: "close", className: "btn-primary", iconName: "check" }],
  }).then(() => undefined);
}

// A message plus a block of the caller's own markup above a Close button -
// the About dialog. `content` is a node, built by the caller with DOM APIs, so
// nothing here parses markup. `hideMessage` keeps the message as the dialog's
// accessible name but off screen, for content that already says what it is
// (a logo saying "Caravel" under a line saying "About Caravel" would say it
// twice). `className` is added to the <dialog> so the content can be styled.
export function contentDialog({ messageKey, message, content, hideMessage = false, className }) {
  return open({
    messageKey,
    message,
    content,
    hideMessage,
    className,
    buttons: [{ labelKey: "common.close", value: "close", className: "btn-primary", iconName: "check" }],
  }).then(() => undefined);
}

let nextId = 0;

// `message` (a ready string) wins over `messageKey` when both are given, for
// the rare caller that has to interpolate.
function open({ messageKey, message, buttons, input, select, content, hideMessage, className }) {
  const dialog = document.createElement("dialog");
  dialog.className = className ? `dialog ${className}` : "dialog";
  const messageId = `dialog-message-${nextId++}`;
  dialog.setAttribute("aria-labelledby", messageId);

  const text = document.createElement("p");
  text.className = hideMessage ? "dialog__message sr-only" : "dialog__message";
  text.id = messageId;
  text.textContent = message ?? t(messageKey);
  dialog.appendChild(text);

  if (content) dialog.appendChild(content);

  let inputEl = null;
  if (input) {
    inputEl = document.createElement("input");
    inputEl.type = "text";
    inputEl.className = "dialog__input";
    inputEl.value = input.value ?? "";
    if (input.placeholderKey) inputEl.placeholder = t(input.placeholderKey);
    // Enter submits, the way it would in a form. The dialog has no <form>, so
    // this is the only thing making the keyboard path work - without it Enter
    // would fall through to <dialog>'s own default button, which is Cancel.
    inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        dialog.close("confirm");
      }
    });
    dialog.appendChild(inputEl);
  }

  let selectEl = null;
  if (select) {
    selectEl = document.createElement("select");
    selectEl.className = "dialog__select";
    for (const option of select.options) {
      const el = document.createElement("option");
      el.value = option.value;
      // textContent, not innerHTML: a label can be a location title somebody
      // typed, and the same rule the menu component follows applies here.
      el.textContent = option.label;
      selectEl.appendChild(el);
    }
    selectEl.value = select.value ?? "";
    // The message is the field's label. It is already the dialog's accessible
    // name through aria-labelledby; pointing the select at it too means the
    // control is named rather than being an unlabelled combobox.
    if (select.labelKey) selectEl.setAttribute("aria-label", t(select.labelKey));
    else selectEl.setAttribute("aria-labelledby", messageId);
    // Enter confirms, matching the input path above -- without it Enter falls
    // through to <dialog>'s default button, which is Cancel.
    selectEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        dialog.close("confirm");
      }
    });
    dialog.appendChild(selectEl);
  }

  const actions = document.createElement("div");
  actions.className = "dialog__actions";
  for (const button of buttons) {
    const el = document.createElement("button");
    el.type = "button";
    el.className = `btn ${button.className}`;
    el.value = button.value;
    el.innerHTML = `${icon(button.iconName)} <span></span>`;
    // textContent on the span rather than into the template, so a translated
    // label can never be parsed as markup.
    el.querySelector("span").textContent = t(button.labelKey);
    el.addEventListener("click", () => dialog.close(button.value));
    actions.appendChild(el);
  }
  dialog.appendChild(actions);

  document.body.appendChild(dialog);

  // Back closes the dialog as a dismissal. While it is open the modal blocks
  // every click, so history is the only way off the page underneath, and a
  // dialog that survived it would let its caller resume on the next page -- a
  // confirmed delete landing after the user had already left. Leaving by any
  // other route would first need the dialog closed, so this one listener
  // covers every caller.
  const showing = new AbortController();
  window.addEventListener("popstate", () => dialog.close(), { signal: showing.signal });

  return new Promise((resolve) => {
    // One "close" event covers every exit: a button, Escape (which the
    // browser turns into cancel + close with an empty returnValue), or Back.
    dialog.addEventListener("close", () => {
      showing.abort();
      const value = dialog.returnValue;
      const text = inputEl?.value ?? "";
      const chosen = selectEl?.value ?? "";
      dialog.remove();
      // With an input or a select, the answer *is* what it holds - or null when
      // dismissed, so "saved an empty string" stays distinguishable from
      // "cancelled".
      if (inputEl) resolve(value === "confirm" ? text : null);
      else if (selectEl) resolve(value === "confirm" ? chosen : null);
      else resolve(value);
    });
    dialog.showModal();
    // The field, not the first button: the point of the dialog is to answer
    // with it, and <dialog> autofocuses the first focusable child otherwise.
    inputEl?.focus();
    inputEl?.select();
    selectEl?.focus();
    // Content can hold links, and <dialog> would focus the first of them: a
    // ring on a link nobody chose, and Enter following it to another tab.
    // Close is the one control every content dialog has.
    if (content) actions.querySelector("button")?.focus();
  });
}
